<template>
  <div class="p-6 mx-auto my-8 max-w-3xl font-sans text-gray-800 bg-white rounded-2xl shadow-lg">
    <h1 class="mb-2 text-3xl font-extrabold text-center text-green-900 md:text-4xl">BoxBuddy Privacy Policy</h1>
    <div class="flex flex-col gap-4 justify-center mb-8 text-sm text-gray-500 sm:flex-row">
      <span><span class="font-semibold">Effective Date:</span> July 15, 2025</span>
      <span><span class="font-semibold">Last Updated:</span> July 15, 2025</span>
    </div>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">1. Introduction</h2>
      <p>BoxBuddy ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our mobile application, website, and services (collectively, the "Services").</p>
      <p>By using our Services, you consent to the collection and use of your information as described in this Privacy Policy. If you do not agree with this Privacy Policy, please do not use our Services.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">2. Information We Collect</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">2.1 Personal Information You Provide</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Create an account (name, email address, phone number, password)</li>
        <li>Place orders (delivery address, payment information)</li>
        <li>Contact customer support</li>
        <li>Leave reviews and ratings</li>
        <li>Participate in promotions or surveys</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">2.2 Payment Information</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Credit/debit card numbers, expiration dates, and security codes</li>
        <li>Digital wallet information (Apple Pay, Google Pay, etc.)</li>
        <li>Billing addresses and payment history</li>
        <li><span class="font-semibold">Note:</span> We use secure third-party payment processors and do not store complete payment card information on our servers</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">2.3 Location Information</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Precise Location: GPS coordinates when you use location services for delivery</li>
        <li>Approximate Location: IP address-based location for service availability</li>
        <li>Address Information: Delivery addresses you provide</li>
        <li>You can control location sharing through your device settings</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">2.4 Device and Usage Information</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Device type, operating system, and version</li>
        <li>App usage patterns and preferences</li>
        <li>IP address and browser information</li>
        <li>Unique device identifiers</li>
        <li>App performance and crash reports</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">2.5 Order and Transaction Data</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Order history and preferences</li>
        <li>Restaurant and cuisine preferences</li>
        <li>Delivery instructions and special requests</li>
        <li>Order timing and frequency patterns</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">2.6 Communications</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Customer support conversations</li>
        <li>Messages between you and delivery partners</li>
        <li>Push notification preferences</li>
        <li>Marketing communication preferences</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">3. How We Use Your Information</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">3.1 Service Provision</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Process and fulfill food orders</li>
        <li>Facilitate delivery services</li>
        <li>Communicate order status and updates</li>
        <li>Provide customer support</li>
        <li>Process payments and refunds</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">3.2 Service Improvement</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Analyze usage patterns to improve our Services</li>
        <li>Personalize your experience and recommendations</li>
        <li>Develop new features and services</li>
        <li>Conduct research and analytics</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">3.3 Communications</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Send order confirmations and delivery updates</li>
        <li>Provide customer support responses</li>
        <li>Send promotional offers and marketing communications (with your consent)</li>
        <li>Notify you of service changes or important updates</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">3.4 Legal and Security</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Comply with legal obligations</li>
        <li>Prevent fraud and abuse</li>
        <li>Protect the security of our Services</li>
        <li>Enforce our Terms of Service</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">4. Information Sharing and Disclosure</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">4.1 Service Providers</h3>
      <p>We share information with third parties who help us provide our Services:</p>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Payment processors for transaction processing</li>
        <li>Delivery partners (limited to information necessary for delivery)</li>
        <li>Restaurant partners (order details and customer preferences)</li>
        <li>Technology providers (cloud hosting, analytics, customer support)</li>
        <li>Marketing partners (with your consent)</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">4.2 Legal Requirements</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Comply with legal processes or government requests</li>
        <li>Protect our rights, property, or safety</li>
        <li>Prevent fraud or illegal activities</li>
        <li>Respond to emergencies</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">4.3 Business Transfers</h3>
      <p>If BoxBuddy is involved in a merger, acquisition, or sale of assets, your information may be transferred to the new entity.</p>
      <h3 class="mt-4 mb-1 text-base font-semibold">4.4 With Your Consent</h3>
      <p>We may share information for other purposes with your explicit consent.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">5. Data Retention</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">5.1 Retention Periods</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Account Information: Retained while your account is active and for a reasonable period after closure</li>
        <li>Order History: Retained for 7 years for tax and legal compliance</li>
        <li>Payment Information: Retained according to payment processor policies</li>
        <li>Communications: Retained for 3 years for customer service purposes</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">5.2 Data Deletion</h3>
      <p>You may request deletion of your personal information, subject to legal and business requirements. Some information may be retained in anonymized form for analytics purposes.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">6. Your Privacy Rights</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">6.1 Access and Control</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Access your personal information</li>
        <li>Update or correct inaccurate information</li>
        <li>Delete your account and associated data</li>
        <li>Export your data in a portable format</li>
        <li>Opt out of marketing communications</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">6.2 Location Services</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Device settings (GPS access)</li>
        <li>App settings (location preferences)</li>
        <li>Account settings (saved addresses)</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">6.3 Marketing Communications</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Using unsubscribe links in emails</li>
        <li>Adjusting push notification settings</li>
        <li>Contacting customer support</li>
        <li>Updating your account preferences</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">6.4 Cookies and Tracking</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Browser settings</li>
        <li>App settings</li>
        <li>Third-party opt-out tools</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">7. Data Security</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">7.1 Security Measures</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Encryption in transit and at rest</li>
        <li>Access controls and authentication</li>
        <li>Regular security assessments</li>
        <li>Employee training on data protection</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">7.2 Data Breaches</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Notify affected users promptly</li>
        <li>Report to relevant authorities as required</li>
        <li>Take immediate steps to secure compromised systems</li>
        <li>Provide updates on our response efforts</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">8. International Data Transfers</h2>
      <p>If you are located outside of [Your Country], please note that your information may be transferred to and processed in countries with different data protection laws. We ensure appropriate safeguards are in place for such transfers.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">9. Children's Privacy</h2>
      <p>Our Services are not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it promptly.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">10. Third-Party Services</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">10.1 Third-Party Links</h3>
      <p>Our Services may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties.</p>
      <h3 class="mt-4 mb-1 text-base font-semibold">10.2 Social Media Integration</h3>
      <p>If you connect your social media accounts to our Services, we may collect information from those platforms according to your privacy settings on those platforms.</p>
      <h3 class="mt-4 mb-1 text-base font-semibold">10.3 Analytics Services</h3>
      <p>We use third-party analytics services that may collect information about your use of our Services. These services have their own privacy policies.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">11. Regional Privacy Laws</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">11.1 California Residents (CCPA)</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Right to know what personal information is collected</li>
        <li>Right to delete personal information</li>
        <li>Right to opt out of the sale of personal information</li>
        <li>Right to non-discrimination for exercising privacy rights</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">11.2 European Union Residents (GDPR)</h3>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Right to access, rectify, and erase personal data</li>
        <li>Right to restrict processing</li>
        <li>Right to data portability</li>
        <li>Right to object to processing</li>
        <li>Right to withdraw consent</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">11.3 Other Jurisdictions</h3>
      <p>We comply with applicable privacy laws in all jurisdictions where we operate.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">12. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Posting the updated policy in our App</li>
        <li>Sending email notifications to registered users</li>
        <li>Displaying prominent notices in our Services</li>
      </ul>
      <p>Continued use of our Services after changes constitutes acceptance of the updated Privacy Policy.</p>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">13. Contact Information</h2>
      <h3 class="mt-4 mb-1 text-base font-semibold">13.1 General Privacy Questions</h3>
      <p>For questions about this Privacy Policy or our privacy practices:</p>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Email: <a href="mailto:support@boxbuddy.me" class="text-green-700 underline">support@boxbuddy.me</a></li>
        <li>Phone: +2347046804856</li>
        <li>Mail: Plot 108, Voera Estate, Arepo, Ogun State, Nigeria</li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">13.2 Data Protection Officer</h3>
      <p>If required by law, our Data Protection Officer can be contacted at:</p>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Email: <a href="mailto:david@boxbuddy.me" class="text-green-700 underline">david@boxbuddy.me</a></li>
      </ul>
      <h3 class="mt-4 mb-1 text-base font-semibold">13.3 Privacy Rights Requests</h3>
      <p>To exercise your privacy rights:</p>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li>Email: <a href="mailto:david@boxbuddy.me" class="text-green-700 underline">david@boxbuddy.me</a></li>
        <li>Use the privacy settings in your account</li>
        <li>Contact customer support through the App</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="pl-3 mb-2 text-xl font-bold text-green-700 border-l-4 border-green-700">14. Definitions</h2>
      <ul class="mb-2 ml-4 space-y-1 list-disc list-inside">
        <li><span class="font-semibold">Personal Information:</span> Information that identifies, relates to, or could reasonably be linked with you</li>
        <li><span class="font-semibold">Processing:</span> Any operation performed on personal information</li>
        <li><span class="font-semibold">Third Party:</span> Any individual or entity other than BoxBuddy</li>
        <li><span class="font-semibold">Services:</span> BoxBuddy's mobile application, website, and related services</li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">

</script>
